// Cart + Product Page
Object.assign(window.HiSnaps, {

    // Product Registry
    // Will parse and populate all available products. 
    allProducts: {},
    activeProductHandle: null,
    getProduct(handle = null) {
        const productHandle = handle || this.activeProductHandle;
        return this.allProducts[productHandle] || null;
    },

    // Page-Level Selection
    // These are not product-specific.
    selectedVariantId: null,
    selectedPlanId: null,
    selectedQuantity: 1,
    isSubscription: false,

    // Cart Status
    cartShowing: false,
    cartEmpty: true,
    cartBtn: $1("header.nav .open-cart"),
    checkoutBtn: $1(".cart-drawer .btn.checkout"),
    addToCartBtns: $$(".add-to-cart"),
    allCartBtns: [],

    cart: {
        async init() {
            // The Shopify cart is a browser cookie, not tied to the customer, so
            // lines added while signed in as a wholesaler would survive sign-out
            // and show up in the retail cart (and vice versa). Clear the cart
            // whenever the session flips between wholesale and retail.
            await this.guardCartMode();

            if (HiSnaps.abTest) {
                await HiSnaps.abTest.init();
            }

            if (!this.parseProductData() || $1("body").classList.contains("page-marketing") || $1("body").classList.contains("contest")) {
                // console.warn("No product data available. So, no cart either. Womp womp.");

                // Just assign the nav add to cart button for a basic redirect.
                // Then we can bail out.
                HiSnaps.cartBtn?.addEventListener("click", async (e) => {
                    e.preventDefault();
                    window.location.href = '/products/drink-boosters';
                });

                return null;
            }
            
            // Cart Buttons + Product Selectors
            await this.initCartButtons();
            this.initProductPickers();
            this.initQuantityControls();
            this.initDescriptionToggle();
            this.initUpsellBanner();
            this.initQuickAdd();

            // Set initial page and cart details.
            this.initializeDefaults();
            this.syncDropdownsFromButtons();
            this.updateSelectedVariant();
            await this.updateCartDrawer();
        },
        async guardCartMode() {
            const mode = document.body.classList.contains("wholesale-mode") ? "wholesale" : "retail";
            const KEY = "hisnaps_cart_mode";
            let stored = null;
            try { stored = localStorage.getItem(KEY); } catch (e) { /* storage unavailable */ }

            if (stored && stored !== mode) {
                try {
                    await fetch('/cart/clear.js', { method: 'POST', headers: { 'Accept': 'application/json' } });
                } catch (e) {
                    console.warn("Cart mode changed but cart clear failed:", e);
                }
            }
            try { localStorage.setItem(KEY, mode); } catch (e) { /* ignore */ }
        },
        parseProductData() {
            const prodDataEls = $$(".product-data");
            if (!prodDataEls || prodDataEls.length == 0) { return false; }

            prodDataEls.forEach(prodDataEl => {
                const productHandle = prodDataEl.dataset.mainProductHandle;
                if (!productHandle) {
                    console.warn("Product data missing handle, skipping:", productHandle);
                    return;
                }

                // Create an entry in the registry for each available product where we have data.
                const hasVariants = prodDataEl.dataset.hasVariants === 'true';
                const productEntry = {
                    mainProductId: prodDataEl.dataset.mainProductId,
                    mainProductHandle: productHandle,
                    variants: [],
                    defaultVariantId: null,
                    optionPositions: null,
                    sellingPlans: [],
                    defaultIsSubscription: false,
                    defaultPlanId: null,
                    mostPopular: null,
                    upsell: null,
                    upsellMessage: null,
                    staticAddBanner: true
                };

                // Variants
                if (hasVariants) {
                    let prodJSON = JSON.parse( prodDataEl.dataset.variants );
                    if (!prodJSON) {
                        this.showCartError();
                        return false;
                    }
                    if (prodJSON.length == 0) {
                        this.showCartError();
                        return false;
                    }
                    
                    // Merge metafields into variants. Unfortunately they are left out of 
                    // standard variant data in liquid so we need to add them manually.
                    const metafields = JSON.parse( prodDataEl.dataset.variantMetafields );
                    prodJSON.forEach(variant => {
                        if (metafields[variant.id]) {
                            variant.metafields = { custom: metafields[variant.id] };
                        }
                    });

                    productEntry.variants = prodJSON;
                    productEntry.defaultVariantId = parseInt(prodDataEl.dataset.defaultVariantId);

                    // Options - map variant option names to their positions.
                    // This is so we don't have to worry about variants being reordered in admin.
                    let optionsJSON = JSON.parse( prodDataEl.dataset.options );
                    if (optionsJSON && optionsJSON.length > 0) {
                        productEntry.optionPositions = {};
                        optionsJSON.forEach((option, index) => {
                            const optionName = option.name.toLowerCase();
                            productEntry.optionPositions[optionName] = `option${index + 1}`;
                        });
                    }

                    // Hidden sizes (theme setting) — mirror the Liquid pickers so a hidden
                    // variant can't be resolved via URL params, defaults, or upsells.
                    const hiddenSizes = (HiSnaps.config?.hiddenSizes || []).map(s => s.toLowerCase());
                    const sizeKey = productEntry.optionPositions?.size;
                    if (hiddenSizes.length && sizeKey) {
                        productEntry.variants = productEntry.variants.filter(v => !hiddenSizes.includes(String(v[sizeKey] || '').toLowerCase()));
                    }
                } else {
                    productEntry.defaultVariantId = parseInt(prodDataEl.dataset.variantId);
                }

                // Subscriptions
                const hasSubscriptions = prodDataEl.dataset.hasSubscriptions;
                if (hasSubscriptions == "true" || hasSubscriptions == true) {
                    let plansJSON = JSON.parse( prodDataEl.dataset.plans );
                    if (!plansJSON) {
                        this.showCartError();
                        return false;
                    }
                    if (plansJSON.length == 0) {
                        this.showCartError();
                        return false;
                    }
                    productEntry.sellingPlans = plansJSON;

                    // Assign default plan ID if we have one. Blank means we'll set one-time purchase.
                    const defaultPlanId = prodDataEl.dataset.defaultPlanId;
                    if (defaultPlanId && defaultPlanId !== "") {
                        productEntry.defaultIsSubscription = true;
                        productEntry.defaultPlanId = parseInt(defaultPlanId);
                    } else {
                        // Default to one-time purchase
                        productEntry.defaultIsSubscription = false;
                        productEntry.defaultPlanId = null;
                    }
                }

                // Product Upsell
                productEntry.mostPopular = parseInt(prodDataEl.dataset.mostPopular);
                productEntry.upsell = parseInt(prodDataEl.dataset.upsell);
                productEntry.upsellMessage = prodDataEl.dataset.upsellMessage;

                // All set. Store this bad girl in the registry.
                HiSnaps.allProducts[productHandle] = productEntry;
            });

            // Remove markup from DOM ... just for cleanliness, and for low level snoopers.
            // prodDataEl.remove();

            // Set the active product.
            HiSnaps.activeProductHandle = $1("body").dataset.activeProductHandle || 'drink-boosters';

            return Object.keys(HiSnaps.allProducts).length > 0;
        },

        // Various Cart Buttons
        async initCartButtons() {
            if (HiSnaps.cartBtn) { HiSnaps.allCartBtns.push(HiSnaps.cartBtn) }
            if (HiSnaps.addToCartBtns) { HiSnaps.allCartBtns.push(...HiSnaps.addToCartBtns) }
            if (HiSnaps.checkoutBtn) { HiSnaps.allCartBtns.push(HiSnaps.checkoutBtn) }

            // Nav Cart Button
            HiSnaps.cartBtn?.addEventListener("click", async (e) => {
                e.preventDefault();

                // If we're on marketing page, just redirect.
                if ($1("body").classList.contains("page-marketing")) {
                    window.location.href = '/products/drink-boosters';
                    return;
                }

                // Empty cart + "Buy Now" in the header: send them shopping instead of
                // opening an empty drawer. Already on a collection page? Scroll to
                // the top and give the collection pills a little wiggle.
                if (HiSnaps.cartEmpty) {
                    const pills = $$("body.page-collection .collection-nav .btn");
                    if (pills.length) {
                        window.scrollTo({ top: 0, behavior: 'smooth' });
                        pills.forEach((pill, i) => {
                            pill.style.setProperty('--attention-index', i);
                            pill.classList.remove("attention");
                            void pill.offsetWidth; // restart the animation on repeat taps
                            pill.classList.add("attention");
                            pill.addEventListener("animationend", () => pill.classList.remove("attention"), { once: true });
                        });
                    } else {
                        window.location.href = '/collections/all';
                    }
                    return;
                }

                this.showCart(!HiSnaps.cartShowing);
            });

            // Add to Cart Buttons
            HiSnaps.addToCartBtns.forEach(btn => {
                btn.addEventListener("click", async (e) => {
                    e.preventDefault();

                    try {
                        await this.addToCart();
                        if (!HiSnaps.cartShowing) {
                            setTimeout(() => {
                                this.showCart(true);
                            }, 690);
                        }
                    } catch (error) {
                        console.error("Error adding to cart: ", error);
                        this.showCartError();
                    }
                });
            });

            // Checkout
            if (HiSnaps.checkoutBtn) {
                HiSnaps.checkoutBtn.addEventListener("click", (e) => {
                    e.preventDefault();
                    
                    // Don't allow checkout if cart is empty
                    if (HiSnaps.cartEmpty) {
                        console.warn("Cannot checkout with empty cart");
                        return;
                    }
                    
                    // Report checkout intent to PostHog before we hand off. Checkout is a
                    // separate Shopify domain the theme can't touch, so this is the last
                    // storefront event; completion is tracked by the Klaviyo funnel pixel.
                    if (window.posthog) {
                        const subText = $1(".cart-drawer .subtotal .value")?.textContent || "";
                        posthog.capture("checkout_started", {
                            source:   "cart_drawer",
                            subtotal: parseFloat(subText.replace(/[^0-9.]/g, "")) || null,
                            currency: "USD",
                        });
                    }

                    // Redirect to Shopify checkout.
                    // Shopify + Seal + Bankful handle everything from here! Godspeed.
                    this.showLoading(true);
                    window.location.href = '/checkout';
                });
            }
            
            // Cart close/continue buttons.
            $$(".cart-drawer .close, .cart-drawer .continue").forEach(btn => {
                btn.addEventListener("click", (e) => {
                    e.preventDefault();
                    this.showCart(false);
                });
            });
        },

        // Hide/Show Cart
        showCart(show) {
            if (show == HiSnaps.cartShowing) { return }
            HiSnaps.cartShowing = show;

            if (show) {
                document.body.classList.add("show-cart");
            } else {
                document.body.classList.add("hiding-cart");
                document.body.classList.remove("show-cart");
                setTimeout(function() {
                    document.body.classList.remove("hiding-cart");
                }, 500);
            }
        },

        // -- Product Selection + UI Updates --

        initProductPickers() {
            const product = HiSnaps.getProduct();
            if (!product || product.variants.length == 0) return;

            // Variants — button scroller (desktop). Source of truth for selection.
            $$(".picker").forEach(picker => {
                picker.querySelectorAll(".btn").forEach(btn => {
                    btn.addEventListener("click", (e) => {
                        e.preventDefault();

                        // Update UI
                        picker.querySelectorAll(".btn").forEach(b => b.classList.remove("selected"));
                        btn.classList.add("selected");

                        // Mirror the choice into the matching dropdown, then resolve.
                        this.syncDropdownToOption(picker.dataset.variantOption, btn.dataset.value);
                        this.updateSelectedVariant();
                    });
                });
            });

            // Variants — dropdown (mobile). Mirrors the buttons; same open/close
            // behavior as the subscription frequency selector.
            $$(".variant-select").forEach(dropdown => {
                const optionName = dropdown.dataset.variantOption;
                const selection = dropdown.querySelector(".selection");

                dropdown.querySelector(".current-selection")?.addEventListener("click", () => {
                    selection.classList.toggle("selected");
                    if (selection.classList.contains("selected")) this.scrollDropdownIntoView(dropdown);
                });

                dropdown.querySelectorAll(".option").forEach(option => {
                    option.addEventListener("click", () => {
                        selection.classList.remove("selected");

                        const value = option.dataset.value;
                        this.syncButtonsToOption(optionName, value);
                        this.syncDropdownToOption(optionName, value);
                        this.updateSelectedVariant();
                    });
                });
            });

            // Mark most popular variant.
            if (product.mostPopular) {
                const mostPopularVariant = product.variants.find(v => v.id === product.mostPopular);
                if (mostPopularVariant) {
                    const sizePosition = product.optionPositions?.size;
                    if (sizePosition) {
                        const sizeValue = mostPopularVariant[sizePosition];
                        const sizePicker = $1('.picker[data-variant-option="size"]');
                        if (sizePicker) {
                            const popularBtn = sizePicker.querySelector(`.btn[data-value="${sizeValue}"]`);
                            if (popularBtn) {
                                popularBtn.classList.add("most-popular");
                            }
                        }
                    }
                }
            }

            // Payment Choice
            const productPlanSelector = $1("#product-plan-selector");
            $$("[data-purchase-type]").forEach(option => {
                option.addEventListener("click", (e) => {
                    e.preventDefault();

                    $$("[data-purchase-type]").forEach(opt => opt.classList.remove("selected"));
                    option.classList.add("selected");

                    const purchaseType = option.dataset.purchaseType.trim().toLowerCase();
                    HiSnaps.isSubscription = (purchaseType === "subscription");

                    if (HiSnaps.isSubscription) {
                        if (productPlanSelector) productPlanSelector.removeAttribute("hidden");
                        const selectedOption = productPlanSelector?.querySelector(".option.selected");
                        const planId = selectedOption ? parseInt(selectedOption.dataset.planId) : null;
                        const defaultPlan = product.sellingPlans.find(p => p.name === "Every Month") ?? product.sellingPlans[0];
                        HiSnaps.selectedPlanId = planId ?? defaultPlan.id;
                    } else {
                        if (productPlanSelector) productPlanSelector.setAttribute("hidden", "");
                        HiSnaps.selectedPlanId = null;
                    }

                    this.setStockUp(purchaseType === "stockup");
                });
            });

            // Product page plan selector — same open/close + select behavior as cart
            if (productPlanSelector) {
                const selection = productPlanSelector.querySelector(".selection");
                const currentLabel = productPlanSelector.querySelector(".current-selection .label");
                const currentSavings = productPlanSelector.querySelector(".current-selection .savings-percent");

                productPlanSelector.querySelector(".current-selection").addEventListener("click", () => {
                    selection.classList.toggle("selected");
                    if (selection.classList.contains("selected")) this.scrollDropdownIntoView(productPlanSelector);
                });

                productPlanSelector.querySelectorAll(".option").forEach(option => {
                    option.addEventListener("click", () => {
                        if (option.classList.contains("selected")) {
                            selection.classList.remove("selected");
                            return;
                        }
                        productPlanSelector.querySelectorAll(".option").forEach(o => o.classList.remove("selected"));
                        option.classList.add("selected");
                        selection.classList.remove("selected");
                        if (currentLabel) currentLabel.textContent = option.querySelector(".plan-name").textContent;
                        if (currentSavings) currentSavings.textContent = option.querySelector(".savings-percent").textContent;
                        HiSnaps.selectedPlanId = parseInt(option.dataset.planId);
                    });
                });
            }
        },

        // -- Variant button <-> dropdown sync --
        // Both markups stay in sync at all times, so the breakpoint-hidden one is
        // always correct when it becomes visible (no resize handling needed).
        syncButtonsToOption(optionName, value) {
            const picker = $1(`.picker[data-variant-option="${optionName}"]`);
            if (!picker) return;
            picker.querySelectorAll(".btn").forEach(btn => {
                btn.classList.toggle("selected", btn.dataset.value === value);
            });
        },
        syncDropdownToOption(optionName, value) {
            const dropdown = $1(`.variant-select[data-variant-option="${optionName}"]`);
            if (!dropdown) return;
            dropdown.querySelectorAll(".option").forEach(option => {
                option.classList.toggle("selected", option.dataset.value === value);
            });
            const label = dropdown.querySelector(".current-selection .label");
            if (label) label.textContent = value;
        },
        syncDropdownsFromButtons() {
            $$(".variant-select").forEach(dropdown => {
                const optionName = dropdown.dataset.variantOption;
                const picker = $1(`.picker[data-variant-option="${optionName}"]`);
                const selectedBtn = picker?.querySelector(".btn.selected");
                if (selectedBtn) this.syncDropdownToOption(optionName, selectedBtn.dataset.value);
                // Reveal the options now that the dropdown is wired and synced.
                dropdown.classList.add("ready");
            });
        },
        scrollDropdownIntoView(el) {
            // On mobile, bring a freshly-opened in-page dropdown to the middle of
            // the viewport so its options aren't cut off below the fold.
            if (window.innerWidth >= 1000) return;
            el.scrollIntoView({ behavior: "smooth", block: "center" });
        },

        initQuantityControls() {
            const quantityInput = $1(".buy-now .cluster.quantity input");
            const addBtn = $1(".cart-qty--add");
            const removeBtn = $1(".cart-qty--remove");

            if (!quantityInput) return;

            // In Stock Up mode the step is the bundle size and the quantity is always a
            // whole number of bundles (typed values round up to the next one).
            const step = () => (HiSnaps.isStockUp && HiSnaps.stockUpQty > 0) ? HiSnaps.stockUpQty : 1;

            const updateQuantity = (newQuantity) => {
                let qty = Math.max(1, parseInt(newQuantity) || 1);
                qty = Math.ceil(qty / step()) * step();
                quantityInput.value = qty;
                HiSnaps.selectedQuantity = qty;

                if (removeBtn) {
                    if (qty <= step()) removeBtn.classList.add("disabled");
                    else removeBtn.classList.remove("disabled");
                }
            };
            HiSnaps.setQuantity = updateQuantity;

            // + button
            if (addBtn) {
                addBtn.addEventListener("click", (e) => {
                    e.preventDefault();
                    updateQuantity(parseInt(quantityInput.value) + step());
                });
            }

            // - button
            if (removeBtn) {
                removeBtn.addEventListener("click", (e) => {
                    e.preventDefault();
                    if (removeBtn.classList.contains("disabled")) return;
                    const newQty = Math.max(1, parseInt(quantityInput.value) - step());
                    updateQuantity(newQty);
                });
            }

            // Input text change.
            quantityInput.addEventListener("change", (e) => {
                updateQuantity(e.target.value);
            });
            
            // Set the initial value.
            updateQuantity(quantityInput.value);
        },
        initDescriptionToggle() {
            const descriptionEl = $1(".product .description");
            if (!descriptionEl) return;
            
            descriptionEl.addEventListener("click", (e) => {
                descriptionEl.classList.toggle("show");
            });
        },
        initUpsellBanner() {
            const product = HiSnaps.getProduct();

            const upsellBanner = $1(".cart-drawer .upsell-banner.upgrade");
            if (upsellBanner && !product.staticAddBanner) {
                // Find the upsell variant data.
                const upsellVariantId = product.upsell;
                if (!upsellVariantId) return;
                const upsellVariant = product.variants.find(v => v.id === upsellVariantId);
                if (!upsellVariant) return;

                // console.log(upsellVariant);

                // Image, title, and description.
                const imageEl = upsellBanner.querySelector(".image");
                if (imageEl && upsellVariant.featured_image.src) {
                    imageEl.innerHTML = `<img src="${upsellVariant.featured_image.src}" alt="${upsellVariant.name}" />`;
                }
                const titleEl = upsellBanner.querySelector(".title");
                if (titleEl) {
                    if (upsellVariant.option1) titleEl.textContent = upsellVariant.option1;
                    else titleEl.textContent = upsellVariant.public_title;
                }
                const descriptionEl = upsellBanner.querySelector(".description");
                if (descriptionEl) {
                    descriptionEl.textContent = product.upsellMessage;
                }

                // Show most popular badge if this variant is also the most popular.
                const mostPopularId = product.mostPopular;
                if (mostPopularId === upsellVariantId) {
                    const popularEl = upsellBanner.querySelector(".popular");
                    if (popularEl) {
                        popularEl.classList.remove("hide");
                    }
                }
                
                const upgradeBtn = upsellBanner.querySelector(".btn.upgrade");
                if (upgradeBtn) {
                    upgradeBtn.addEventListener("click", async (e) => {
                        e.preventDefault();
                        await this.upgradeToUpsell();
                    });
                }
            }

            // Handle upgrade/add button taps.
            const addBanner = $1(".cart-drawer .upsell-banner.add");
            if (addBanner && product.staticAddBanner) {
                const addBtn = addBanner.querySelector(".btn.upgrade-add");
                if (addBtn) {
                    addBtn.addEventListener("click", async (e) => {
                        e.preventDefault();
                        await this.addUpsellItem();
                    });
                }
            }
        },
        async addUpsellItem() {
            this.showLoading(true);

            // Not as bad as upgrade, but still a bit of a doozy!
            try {
                // TODO: May need to adjust this for flavors later?
                // Hard-coded to 10-pack variant.
                const tenPackVariantId = 46700301484253;

                // First, need to get the current cart to check for selling plans.
                const cartResponse = await fetch('/cart.js');
                const cart = await cartResponse.json();
                let sellingPlanId = null;
                if (cart.items.length > 0 && cart.items[0].selling_plan_allocation) {
                    // Just use the first item, in case there are multiple and they vary.
                    sellingPlanId = cart.items[0].selling_plan_allocation.selling_plan.id;
                }

                // Then, check if 10-pack already exists in cart.
                const existingItem = cart.items.find(item => item.variant_id === tenPackVariantId);
                if (existingItem) {
                    // If so, just increment the quantity.
                    const line = cart.items.indexOf(existingItem) + 1;
                    await this.updateCartItem(line, { quantity: existingItem.quantity + 1 });
                } else {
                    // If not, add a new 10-pack to cart.
                    const addData = {
                        items: [{
                            id: tenPackVariantId,
                            quantity: 1
                        }]
                    };
                    // And respect the current selling plan.
                    if (sellingPlanId) {
                        addData.items[0].selling_plan = sellingPlanId;
                    }
                    const addResponse = await fetch('/cart/add.js', {
                        method: 'POST',
                        headers: { 'Content-Type': 'application/json' },
                        body: JSON.stringify(addData)
                    });

                    if (!addResponse.ok) {
                        throw new Error('Failed to add 10-pack');
                    }

                    await this.updateCartDrawer();
                }

                HiSnaps.confettiMe( $1("header.nav .open-cart") );

            } catch (error) {
                console.error('Error adding 10-pack:', error);
                this.showCartError();
            } finally {
                this.showLoading(false);
            }
        },
        async upgradeToUpsell() {
            const product = HiSnaps.getProduct();

            this.showLoading(true);

            // Okay, this one is a doozy...!

            try {
                // First, need to get the current cart to check for selling plans.
                const cartResponse = await fetch('/cart.js');
                const cart = await cartResponse.json();
                let sellingPlanId = null;
                if (cart.items.length > 0 && cart.items[0].selling_plan_allocation) {
                    // Just use the first item, in case there are multiple and they vary.
                    sellingPlanId = cart.items[0].selling_plan_allocation.selling_plan.id;
                }

                // Now, clear the cart...
                const clearResponse = await fetch('/cart/clear.js', {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' }
                });
                if (!clearResponse.ok) {
                    throw new Error('Failed to clear cart');
                }

                // Add the upsell variant...
                const upsellVariantId = product.upsell;
                const addData = {
                    items: [{
                        id: upsellVariantId,
                        quantity: 1
                    }]
                };
                // ...with the same selling plan if have one.
                if (sellingPlanId) {
                    addData.items[0].selling_plan = sellingPlanId;
                }
                const addResponse = await fetch('/cart/add.js', {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify(addData)
                });
                if (!addResponse.ok) {
                    throw new Error('Failed to add upsell item');
                }

                // Update the cart, and we're off!
                await this.updateCartDrawer();
                HiSnaps.confettiMe($1("header.nav .open-cart"));

            } catch (error) {
                console.error('Error upgrading to upsell:', error);
                this.showCartError();
            } finally {
                this.showLoading(false);
            }
        },
        updateSelectedVariant() {
            const product = HiSnaps.getProduct()
            if (!product) return;

            // No variants, just auto-select the first ID and we're done.
            if (product.variants.length == 0) {
                HiSnaps.selectedVariantId = product.defaultVariantId;
                return;
            }

            // Check which pickers exist on the page
            const sizePicker = $1('.picker[data-variant-option="size"]');
            const flavorPicker = $1('.picker[data-variant-option="flavor"]');
            
            // Away from product page, just use the defaults.
            if (!document.body.classList.contains("page-product")) {
                const defaultVariant = product.variants.find(v => 
                    v.id === product.defaultVariantId
                );
                if (defaultVariant) {
                    HiSnaps.selectedVariantId = defaultVariant.id;
                }
                return;
            }
        
            // On product page - get selected values from any pickers that exist.
            const sizeBtn = sizePicker?.querySelector(".btn.selected");
            const flavorBtn = flavorPicker?.querySelector(".btn.selected");
            
            // Build the variant query based on what's available.
            const sizePos = product.optionPositions?.size || 'option1';
            const flavorPos = product.optionPositions?.flavor || 'option2';
            
            let variant;
            if (sizeBtn && flavorBtn) {
                const selectedSize = sizeBtn.dataset.value;
                const selectedFlavor = flavorBtn.dataset.value;
                variant = product.variants.find(v => 
                    v[sizePos] === selectedSize && v[flavorPos] === selectedFlavor
                );
            } else if (sizeBtn) {
                const selectedSize = sizeBtn.dataset.value;
                variant = product.variants.find(v => 
                    v[sizePos] === selectedSize
                );
            } else if (flavorBtn) {
                const selectedFlavor = flavorBtn.dataset.value;
                variant = product.variants.find(v => 
                    v[flavorPos] === selectedFlavor
                );
            } else {
                // No pickers - use the defaults.
                variant = product.variants.find(v => 
                    v.id === product.defaultVariantId
                );
            }

            if (variant) {
                HiSnaps.selectedVariantId = variant.id;
                this.updateProductPageDisplay(variant);
            } else {
                console.warn("No matching variant found");
            }

            // If we have any galleries on the page, notify them of the change.
            const gallery = $1(".media-gallery");
            if (gallery) {
                gallery.dispatchEvent(new CustomEvent("HiSnaps.variantChanged"));
            }

            // And scroll the selected item into view.
            $$(".picker .btn.selected").forEach(btn => {
                this.scrollButtonIntoView(btn)
            });
        },
        scrollButtonIntoView(btn, smooth = true) {
            const pickerWrap = btn.closest(".picker-wrap");
            if (!pickerWrap) return;
            
            const containerRect = pickerWrap.getBoundingClientRect();
            const btnRect = btn.getBoundingClientRect();
            const btnCenter = btnRect.left + (btnRect.width / 2);
            const containerCenter = containerRect.left + (containerRect.width / 2);
            const scrollOffset = btnCenter - containerCenter;
            
            pickerWrap.scrollBy({
                left: scrollOffset,
                behavior: smooth ? "smooth" : "auto"
            });
        },
        updateProductPageDisplay(variant) {
            const product = HiSnaps.getProduct();

            const unitCount = variant.metafields?.custom?.unit_count;
            
            // Description
            const variantDescription = variant.metafields?.custom?.variant_description;
            const descriptionEl = $1(".product .description");
            const variantDescriptionEl = $1(".product .description .variant");
            if (descriptionEl) {
                if (variantDescription && descriptionEl && variantDescriptionEl) {
                    variantDescriptionEl.innerHTML = variantDescription.trim();
                    descriptionEl.classList.add("show-variant");
                } else {
                    descriptionEl.classList.remove("show-variant");
                }
            }

            // What's Included
            const whatsIncluded = variant.metafields?.custom?.whats_included;
            if (whatsIncluded) {
                $$(".accordion .whats-included .content").forEach(el => {
                    el.innerHTML = whatsIncluded.trim();
                });
            }

            // One-Time
            this.updatePrice('[data-purchase-type="onetime"] .total', variant.price);
            if (unitCount) {
                this.updatePrice('[data-purchase-type="onetime"] .per-unit', variant.price / unitCount, true);
            }

            // Subscription
            if (product.sellingPlans.length > 0) {
                // Find allocation matching the selected/default plan.
                let allocation;
                if (HiSnaps.selectedPlanId) {
                    // Note: variant allocations use selling_plan_id, not selling_plan.id ... ugh.
                    allocation = variant.selling_plan_allocations.find(a => 
                        a.selling_plan_id === HiSnaps.selectedPlanId
                    );
                }
                // Fallback to first allocation if we didn't find a match.
                if (!allocation) {
                    allocation = variant.selling_plan_allocations[0];
                }

                if (allocation) {
                    this.updatePrice('[data-purchase-type="subscription"] .total', allocation.per_delivery_price);
                    if (unitCount) {
                        this.updatePrice('[data-purchase-type="subscription"] .per-unit', allocation.per_delivery_price / unitCount, true);
                    }
            
                    // Savings
                    const savings = variant.price - allocation.per_delivery_price;
                    this.updatePrice('[data-purchase-type="subscription"] .highlight', savings);
                }
            }

            // Holiday Stock-Up overlay (after the base prices are in place).
            this.updatePromoDisplay(variant);
        },
        updatePrice(selector, priceInCents, isPerUnit = false, wasInCents = null) {
            const el = $1(selector);
            if (!el) return;
            
            const dollars = Math.floor(priceInCents / 100);
            const cents = String(Math.round(priceInCents % 100)).padStart(2, '0');
            
            if (isPerUnit) {
                el.innerHTML = `$${dollars}.<sup class="cents">${cents}</sup> per drink`;
            } else {
                el.innerHTML = `$${dollars}.<sup class="cents">${cents}</sup>`;
                // Optional struck-through "was" price on its own line above (the sibling
                // .was span). Cleared whenever a price is set without one.
                const wasEl = el.parentElement?.querySelector('.was');
                if (wasEl) {
                    wasEl.innerHTML = (wasInCents != null && wasInCents > priceInCents) ? `<s>${this.priceString(Math.round(wasInCents))}</s>` : '';
                }
            }
        },

        // -- Holiday Stock-Up (display only; Shopify automatic discounts do the real math) --

        // The sale offer for this variant's size, or null when the sale is off or the
        // size isn't in it. Rules mirror the admin discounts and come from theme settings
        // (HiSnaps.config.promo.offers). Two kinds:
        //   { kind: 'bundle', name, qty, unitPrice, wasUnitPrice, total, wasTotal, savings, detail }
        //     -> a fixed "stock up" bundle (6 tins, 4 pouches) sold as its own purchase option.
        //   { kind: 'case', name, unitPrice, wasUnitPrice, savings, detail }
        //     -> the size itself is the deal (Case); the one-time price just drops.
        // `unitPrice` is the price the offer is based on (one-time, or per-delivery for subs).
        promoFor(variant, unitPrice) {
            const promo = HiSnaps.config?.promo;
            if (!promo?.active || !variant) return null;
            const o = promo.offers || {};
            const product = HiSnaps.getProduct();
            const sizePos = product?.optionPositions?.size;
            const size = sizePos ? String(variant[sizePos] || '') : '';
            if (!size) return null;
            const same = (a, b) => String(a || '').trim().toLowerCase() === String(b || '').trim().toLowerCase();
            const money = (c) => this.priceString(Math.round(c));
            // What one unit of this size is called ("4 Tins", "4 Cases").
            const lower = size.toLowerCase();
            const unit = same(size, o.tinSize) ? 'tin' : lower.includes('kit') ? 'kit' : lower.includes('case') ? 'case' : 'pouch';
            const units = { tin: 'Tins', kit: 'Kits', case: 'Cases', pouch: 'Pouches' }[unit];

            // Offers are checked in order; a branch whose numbers are zeroed out in
            // theme settings is skipped so the size can fall through to Buy X Get Y.

            // Case: flat dollars off, the size is the deal.
            const caseOff = (parseFloat(o.caseAmountOff) || 0) * 100;
            if (same(size, o.caseSize) && caseOff > 0) {
                const off = caseOff;
                const sale = Math.max(0, unitPrice - off);
                return {
                    kind: 'case', name: 'Holiday Case', qty: 1, unit: 'case',
                    unitPrice: sale, wasUnitPrice: unitPrice,
                    total: sale, wasTotal: unitPrice, savings: off,
                    detail: `&#8618;&#xFE0E; 12 Tins &middot; Save ${money(off)}`
                };
            }

            // Half Case: percent off tins at the minimum quantity.
            const tinQty = parseInt(o.tinMinQty) || 0;
            const tinPct = parseFloat(o.tinPercent) || 0;
            if (same(size, o.tinSize) && tinQty > 0 && tinPct > 0) {
                const qty = tinQty;
                const pct = tinPct;
                const sale = unitPrice * (1 - pct / 100);
                return {
                    kind: 'bundle', name: 'Holiday Half Case', qty, unit,
                    unitPrice: sale, wasUnitPrice: unitPrice,
                    total: sale * qty, wasTotal: unitPrice * qty, savings: (unitPrice - sale) * qty,
                    detail: `&#8618;&#xFE0E; ${qty} ${units} &middot; Save ${money((unitPrice - sale) * qty)}`
                };
            }

            // Buy X get Y free (pouches, kits): one bundle of buy + get.
            if ((o.bxgySizes || []).some(s => same(s, size))) {
                const buy = parseInt(o.bxgyBuy) || 0;
                const get = parseInt(o.bxgyGet) || 0;
                if (buy <= 0 || get <= 0) return null;
                const qty = buy + get;
                const total = unitPrice * buy;
                return {
                    kind: 'bundle', name: `Buy ${buy}, Get ${get} Free`, qty, unit,
                    unitPrice: total / qty, wasUnitPrice: unitPrice,
                    total, wasTotal: unitPrice * qty, savings: unitPrice * get,
                    detail: `&#8618;&#xFE0E; ${qty} ${units} &middot; Save ${money(unitPrice * get)}`
                };
            }

            return null;
        },

        // Fills (or hides) the Stock Up purchase option and the Case note for the
        // selected variant. Called at the end of updateProductPageDisplay, so the
        // base one-time / subscription prices are already in place.
        updatePromoDisplay(variant = null) {
            const choice = $1('.payment-choice');
            if (!choice) return;
            const product = HiSnaps.getProduct();
            if (!variant) variant = product?.variants.find(v => v.id === HiSnaps.selectedVariantId);

            const row = choice.querySelector('[data-purchase-type="stockup"]');
            const onetime = choice.querySelector('[data-purchase-type="onetime"]');
            const note = onetime?.querySelector('.promo-note');
            const promo = HiSnaps.config?.promo;
            const deal = (variant && promo?.active) ? this.promoFor(variant, variant.price) : null;

            // Reset.
            if (note) { note.hidden = true; note.innerHTML = ''; }
            if (row) row.hidden = true;
            if (onetime) onetime.hidden = false;

            // Case: the deal applies from quantity one, so the sale row *replaces*
            // One-Time (a full-price One-Time would be a lie). Rendered like the
            // bundles below with a bundle size of 1.
            if (deal?.kind === 'case') {
                if (onetime) onetime.hidden = true;
                if (onetime?.classList.contains('selected') && row) {
                    onetime.classList.remove('selected');
                    row.classList.add('selected');
                }

                if (promo.subscriptions && product.sellingPlans.length > 0) {
                    const unitCount = variant?.metafields?.custom?.unit_count;
                    const allocation = this.currentAllocation(variant);
                    const subDeal = allocation ? this.promoFor(variant, allocation.per_delivery_price) : null;
                    if (subDeal) {
                        this.updatePrice('[data-purchase-type="subscription"] .total', subDeal.unitPrice, false, subDeal.wasUnitPrice);
                        if (unitCount) this.updatePrice('[data-purchase-type="subscription"] .per-unit', subDeal.unitPrice / unitCount, true);
                    }
                }
            }

            // Any deal (bundle, or the Case with a bundle size of 1): fill the sale row.
            if (deal && row) {
                row.querySelector('.offer-name').textContent = deal.name;
                row.querySelector('.offer-detail').innerHTML = deal.detail;
                this.updatePrice('[data-purchase-type="stockup"] .total', deal.total, false, deal.wasTotal);
                row.dataset.bundleQty = deal.qty;
                row.hidden = false;
            }

            // Keep the selection coherent: if Stock Up is selected but no longer offered
            // (e.g. switched to a size with no deal), fall back to One-Time.
            const stockUpSelected = row && !row.hidden && row.classList.contains('selected');
            const stockUpWanted = HiSnaps.isStockUp || row?.classList.contains('selected');   // incl. selected-by-default while still hidden
            if (stockUpWanted && !stockUpSelected) {
                $$('[data-purchase-type]').forEach(opt => opt.classList.remove('selected'));
                $1('[data-purchase-type="onetime"]')?.classList.add('selected');
                HiSnaps.isSubscription = false;
                HiSnaps.selectedPlanId = null;
                $1('#product-plan-selector')?.setAttribute('hidden', '');
                this.setStockUp(false);
            } else if (stockUpSelected) {
                this.setStockUp(true);
            }
        },

        // Selling-plan allocation for the selected (or first) plan.
        currentAllocation(variant) {
            let allocation = HiSnaps.selectedPlanId
                ? variant.selling_plan_allocations.find(a => a.selling_plan_id === HiSnaps.selectedPlanId)
                : null;
            return allocation || variant.selling_plan_allocations[0] || null;
        },

        // Enter/leave Stock Up mode. While on, the quantity is a multiple of the bundle
        // size (6, 12, 18 tins...) and the stepper moves in bundles; see
        // initQuantityControls. Leaving it returns the quantity to 1 if it's still a
        // whole number of bundles (i.e. we set it).
        setStockUp(on) {
            const row = $1('[data-purchase-type="stockup"]');
            const bundleQty = parseInt(row?.dataset.bundleQty) || 0;
            const wasOn = !!HiSnaps.isStockUp;
            HiSnaps.isStockUp = !!on && bundleQty > 0;
            document.body.classList.toggle('stockup-selected', HiSnaps.isStockUp);

            if (HiSnaps.isStockUp) {
                // New bundle size (first selection, or a size switch): one bundle.
                // Same size: keep the quantity, snapped to a multiple.
                const sizeChanged = HiSnaps.stockUpQty !== bundleQty;
                HiSnaps.stockUpQty = bundleQty;
                HiSnaps.setQuantity?.(sizeChanged ? bundleQty : (HiSnaps.selectedQuantity || bundleQty));
            } else if (wasOn) {
                const step = HiSnaps.stockUpQty || 1;
                HiSnaps.stockUpQty = null;
                if (HiSnaps.selectedQuantity % step === 0) HiSnaps.setQuantity?.(1);
            }
        },

        // -- Cart Management --

        async addToCart() {
            if (!HiSnaps.selectedVariantId) {
                console.warn("No variant selected. Very whimsical.");
                return;
            }

            const formData = {
                items: [{
                    id: HiSnaps.selectedVariantId,
                    quantity: HiSnaps.selectedQuantity
                }]
            };
            if (HiSnaps.isSubscription && HiSnaps.selectedPlanId) {
                formData.items[0].selling_plan = HiSnaps.selectedPlanId;
            }

            await this.postCartAdd(formData, {
                isSubscription: HiSnaps.isSubscription,
                planId: HiSnaps.selectedPlanId || null
            });
        },

        // Shared add flow: POST to the cart, report to PostHog, bridge identity,
        // refresh the drawer, confetti. Used by the product page add-to-cart and
        // the collection cards' quick-add.
        async postCartAdd(formData, { isSubscription = false, planId = null } = {}) {
            this.showLoading(true);

            try {
                const response = await fetch('/cart/add.js', {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify(formData)
                });
        
                if (!response.ok) {
                    throw new Error(`Failed to add to cart: ${response.status}`);
                }

                const result = await response.json();

                // Report the successful add to PostHog with value + subscription state —
                // things autocapture can't see (it only sees the click, not the outcome).
                // result is the added line item, or { items: [...] } for multi-adds.
                if (window.posthog) {
                    const added = (result && result.items) ? result.items[0] : result;
                    const requested = formData.items[0];
                    posthog.capture("add_to_cart", {
                        product_title:   added?.product_title,
                        variant_id:      added?.variant_id || added?.id || requested.id,
                        variant_title:   added?.variant_title,
                        quantity:        added?.quantity || requested.quantity,
                        is_subscription: isSubscription,
                        is_stock_up:     !!HiSnaps.isStockUp,
                        selling_plan_id: planId,
                        price:           (added?.final_price ?? added?.price) / 100,
                        value:           (added?.final_line_price ?? (added?.price * (added?.quantity || 1))) / 100,
                        currency:        "USD",
                    });
                }

                // Bridge our PostHog identity into the cart so it rides through to
                // Shopify checkout — a separate domain where the checkout pixel can't
                // read our cookie. The checkout-funnel pixel reads `_ph_distinct_id`
                // and `_ph_session_id` back and sends its events under this same
                // person + session ($session_id), so the funnel stitches storefront →
                // checkout → order and orders attribute to channel/campaign.
                // Underscore-prefixed keys are hidden from customers and order display.
                if (window.posthog && posthog.get_distinct_id) {
                    try {
                        fetch('/cart/update.js', {
                            method: 'POST',
                            headers: { 'Content-Type': 'application/json' },
                            body: JSON.stringify({
                                attributes: {
                                    '_ph_distinct_id': posthog.get_distinct_id(),
                                    '_ph_session_id':  posthog.get_session_id ? posthog.get_session_id() : ''
                                }
                            })
                        }).catch(() => {});
                    } catch (e) { /* never block add-to-cart */ }
                }

                // Jagshemash!
                await this.updateCartDrawer();
                HiSnaps.confettiMe( $1("header.nav .open-cart") );
                
            } catch (error) {
                console.error('Cart error:', error);
                this.showCartError();
            } finally {
                this.showLoading(false);
            }
        },

        // Collection cards: single-variant products add straight from the card.
        // The card itself is a link to the product page, so the button has to
        // swallow the click. Multi-variant cards have no .quick-add and just
        // follow the link.
        initQuickAdd() {
            $$(".product-card .quick-add").forEach(btn => {
                btn.addEventListener("click", async (e) => {
                    e.preventDefault();
                    e.stopPropagation();

                    const variantId = parseInt(btn.dataset.variantId);
                    if (!variantId || btn.classList.contains("loading")) return;

                    // Only this card's button shows the loader; the shared cart
                    // buttons (nav, checkout) still get theirs via showLoading.
                    btn.classList.add("loading");
                    try {
                        await this.postCartAdd({ items: [{ id: variantId, quantity: 1 }] });
                        this.showCart(true);
                    } finally {
                        btn.classList.remove("loading");
                    }
                });
            });
        },

        // Returns true when the preview code was just applied (caller should refetch).
        async applyPreviewCode(cart) {
            const promo = HiSnaps.config?.promo;
            const code = promo?.preview && promo?.previewCode;
            if (!code || cart.item_count <= 0) return false;
            const KEY = "hisnaps_preview_code";
            try {
                if (sessionStorage.getItem(KEY) === code) return false;
                const res = await fetch('/cart/update.js', {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({ discount: code })
                });
                sessionStorage.setItem(KEY, code);
                return res.ok;
            } catch (e) {
                return false;
            }
        },

        async updateCartDrawer({ reconciled = false } = {}) {
            try {
                const response = await fetch('/cart.js');
                const cart = await response.json();
                
                // console.log("Cart Refreshed:", cart);

                // Update empty state.
                HiSnaps.cartEmpty = (cart.item_count <= 0);

                // Preview mode: apply the mirror discount code once so the cart shows
                // real sale math before the automatic discounts go live. Re-fetches the
                // cart after applying; the session flag keeps it from looping.
                if (await this.applyPreviewCode(cart)) return this.updateCartDrawer();

                // Buy X Get Y: make sure earned free units are in the cart (one pass).
                if (!reconciled && await this.reconcileFreeUnits(cart)) return this.updateCartDrawer({ reconciled: true });

                // Update header and totals.
                const newTotal = this.updateTotals(cart);
                
                // Render cart UI.
                this.renderCartItems(cart);
                this.updateUpsellBanner(cart, newTotal);

                if (HiSnaps.cartEmpty) this.showCart(false);
            } catch (error) {
                console.error('Failed to fetch cart:', error);
            }
        },
        updateTotals(cart) {
            // No header cart button in wholesale mode (see header.liquid).
            if (HiSnaps.cartEmpty) {
                if (HiSnaps.cartBtn) {
                    HiSnaps.cartBtn.querySelector(".label").textContent = "Buy";
                    HiSnaps.cartBtn.classList.remove("cart-full");
                }
                $1(".cart-drawer .total-items").textContent = "";
            } else {
                if (HiSnaps.cartBtn) {
                    HiSnaps.cartBtn.querySelector(".label").textContent = cart.item_count;
                    HiSnaps.cartBtn.classList.add("cart-full");
                }
                $1(".cart-drawer .total-items").textContent = `${cart.item_count} item${(cart.item_count != 1) ? "s" : ""}`;
            }

            // Totals are summed per line rather than read off cart.total_price, which
            // goes stale on price changes. "Original" is the full one-time price;
            // "actual" is Shopify's final_line_price, which already has subscription
            // pricing AND automatic/code discounts (Holiday Stock-Up) baked in.
            let originalTotal = 0;
            let actualTotal = 0;

            cart.items.forEach(item => {
                const { originalLine, finalLine } = this.linePrices(item);
                originalTotal += originalLine;
                actualTotal += finalLine;
            });

            const totalDiscount = originalTotal - actualTotal;
            
            $1(".cart-drawer .original-total .value").innerHTML = `<em>saving ${this.priceString(totalDiscount)}</em> <s>${this.priceString(originalTotal)}</s>`;
            $1(".cart-drawer .discount .value").textContent = `-${this.priceString(totalDiscount)}`;
            $1(".cart-drawer .subtotal .value").textContent = this.priceString(actualTotal);

            if (totalDiscount <= 0) {
                $1(".cart-drawer .totals").classList.remove("has-discount");
            } else {
                $1(".cart-drawer .totals").classList.add("has-discount");
            }

            return actualTotal;
        },
        renderCartItems(cart) {
            const itemsContainer = $1(".cart-drawer .cart-items");
            if (!itemsContainer) return;
            
            if (cart.items.length === 0) {
                itemsContainer.innerHTML = "<p>Your cart is empty.</p>";
                return;
            }
            
            itemsContainer.innerHTML = '';
            
            const template = $1("#cart-item-template");
            // One row per thing the customer added (variant + plan), even when Shopify
            // splits it across lines for a Buy X Get Y discount. See groupCartItems.
            const groups = this.groupCartItems(cart);
            groups.forEach(g => {
                const item = g.item;
                const clone = template.content.cloneNode(true);
                const cartItemProduct = HiSnaps.allProducts[item.handle];

                // Create the cart item... 
                const itemEl = clone.querySelector(".item");
                // Store key (for product updates) and line number (for cart updates).
                itemEl.dataset.key = item.key;
                const line = g.lines[0];
                itemEl.dataset.line = line;    // Ugh, 1-index cart array. Booo. 
                // Group data for key-based updates (multi-line rows) and bundle math.
                itemEl.dataset.keys = JSON.stringify(g.keys);
                itemEl.dataset.variantId = item.variant_id;
                itemEl.dataset.total = g.total;
                if (g.bxgy) itemEl.dataset.bxgy = JSON.stringify(g.bxgy);
                
                // Upsell non-subscription items.
                const isSubscription = item.selling_plan_allocation != null;
                if (!isSubscription) itemEl.classList.add("show-upsell");
                
                // Info
                clone.querySelector(".title a").textContent = item.product_title;
                clone.querySelector(".title a").setAttribute("href", item.url);
                if (cartItemProduct && cartItemProduct.variants.length > 0) {
                    const sizeOption = item.options_with_values.find(o => o.name.toLowerCase() === 'size');
                    const flavorOption = item.options_with_values.find(o => o.name.toLowerCase() === 'flavor');
                    if (sizeOption) clone.querySelector(".size").textContent = sizeOption.value;
                    if (flavorOption) clone.querySelector(".flavor").textContent = flavorOption.value;
                }
                clone.querySelector(".image").innerHTML = `<img src=${item.image}" />`
                clone.querySelector(".image").setAttribute("href", item.url);

                // Price — unit price, struck through when subscription pricing or a
                // discount (automatic or code) brings it down.
                const { originalUnit, finalUnit } = this.linePrices(item);
                if (finalUnit <= 0) {
                    // Only free units in this row (shouldn't happen once grouped, but be safe).
                    clone.querySelector(".price").innerHTML = `<s>${this.priceString(originalUnit)}</s> Free`;
                    itemEl.classList.add("free-line");   // hides the subscription dropdown (would void the deal)
                } else if (finalUnit < originalUnit) {
                    clone.querySelector(".price").innerHTML = `<s>${this.priceString(originalUnit)}</s> ${this.priceString(finalUnit)}`;
                } else {
                    clone.querySelector(".price").textContent = this.priceString(finalUnit);
                }

                // Discounts on this row, by name ("Holiday Half Case"). Amounts are left
                // off — the struck unit price and the Savings total already carry them,
                // and the line gets crowded on mobile. $0 allocations (Buy X Get Y tags
                // the paid units too) are already filtered out.
                const noteEl = clone.querySelector(".discount-note");
                if (noteEl && g.allocations.length) {
                    itemEl.classList.add("has-promo");
                    noteEl.innerHTML = g.allocations.map(a =>
                        `<span class="highlighter theme--grapefruit">${a.title}</span>`
                    ).join('<br />');
                } else if (noteEl) {
                    noteEl.remove();
                }

                // Quantity — for a Buy X Get Y row the stepper counts PAID units; the
                // free ones are shown beside it and derived by Shopify from the total.
                const shownQty = g.bxgy ? g.paid : g.total;
                clone.querySelector(".quantity input").value = shownQty;
                clone.querySelector(".quantity input").dataset.key = item.key;
                clone.querySelector(".quantity input").dataset.line = line;
                clone.querySelector(".remove").dataset.key = item.key;
                clone.querySelector(".remove").dataset.line = line;

                const freeEl = clone.querySelector(".free-units");
                if (freeEl && g.free > 0) {
                    freeEl.textContent = `+${g.free} free`;
                    freeEl.hidden = false;
                }

                // Disable minus by default if we're at 1 quantity.
                const minusBtn = clone.querySelector(".cart-qty--remove");
                if (minusBtn && shownQty <= 1) {
                    minusBtn.classList.add("disabled");
                }
                
                // Subscription Extras
                if (isSubscription) {
                    const savingsPercent = item.selling_plan_allocation.selling_plan.price_adjustments[0].value;
                    clone.querySelector(".savings-percent").textContent = savingsPercent;
                    clone.querySelector(".current-selection .label").textContent = item.selling_plan_allocation.selling_plan.name;
                } else {
                    const variant = cartItemProduct?.variants.find(v => v.id === item.variant_id);
                    let savingsStr = "";
                    if (variant && variant.selling_plan_allocations && variant.selling_plan_allocations.length > 0) {
                        let maxSavings = 0;
                        const currentPrice = variant.price;

                        variant.selling_plan_allocations.forEach(plan => {
                            if (plan.price < currentPrice) {
                                const savings = currentPrice - plan.price;
                                if (savings > maxSavings) maxSavings = savings
                            }
                        });
                        if (maxSavings > 0) {
                            const savingsPercent = Math.round((maxSavings / currentPrice) * 100);
                            savingsStr = ` ${savingsPercent}%`;
                        }
                    }
                    clone.querySelector(".current-selection .label").textContent = `Subscribe & Save${savingsStr}`;
                }

                this.renderPlanOptions(clone, item, line, cartItemProduct);
                itemsContainer.appendChild(clone);
            });
            
            // Handle cart updates.
            this.attachCartItemListeners();
        },
        renderPlanOptions(itemClone, cartItem, line, itemProduct) {
            // We'll hide the selector via CSS if there are no available plans, since it means
            // one-time purchase is the only option.
            if (!itemProduct || !itemProduct.sellingPlans || itemProduct.sellingPlans.length === 0) {
                if (itemClone) itemClone.querySelector(".payment-type").classList.add("hide");
                return;
            }
            
            const planCount = itemProduct.sellingPlans.length + 1;
            itemClone.querySelector(".selection").setAttribute("style", `--numOptions: ${planCount} !important;`)
            const planOptionsContainer = itemClone.querySelector(".payment-type .options");
            const planTemplate = $1("#plan-option-template");
            
            // Subscription Options
            itemProduct.sellingPlans.forEach(plan => {
                const planClone = planTemplate.content.cloneNode(true);
                const optionEl = planClone.querySelector(".option");
                
                optionEl.dataset.planId = plan.id;
                optionEl.dataset.line = line;
                planClone.querySelector(".plan-name").textContent = plan.name;

                const savingsPercent = plan.price_adjustments[0].value;
                planClone.querySelector(".savings-percent").textContent = savingsPercent;
                
                if (cartItem.selling_plan_allocation && cartItem.selling_plan_allocation.selling_plan.id === plan.id) {
                    optionEl.classList.add("selected");
                }
                
                planOptionsContainer.appendChild(planClone);
            });

            // One-Time Purchase
            const oneTimeOption = document.createElement("div");
            oneTimeOption.classList.add("option");
            oneTimeOption.innerHTML = '<div class="plan-name">One Time</div>';
            oneTimeOption.dataset.planId = '';
            oneTimeOption.dataset.line = line;
            if (!cartItem.selling_plan_allocation) {
                oneTimeOption.classList.add("selected");
            }
            planOptionsContainer.appendChild(oneTimeOption);
        },
        attachCartItemListeners() {
            $$(".cart-drawer .quantity input").forEach(input => {
                input.addEventListener("change", (e) => {
                    this.setRowQuantity(e.target.closest(".item"), parseInt(e.target.value));
                });
            });

            $$(".cart-drawer .item .cart-qty--add").forEach(btn => {
                btn.addEventListener("click", (e) => {
                    e.preventDefault();
                    const input = btn.closest(".cluster.quantity").querySelector("input");
                    this.setRowQuantity(btn.closest(".item"), parseInt(input.value) + 1);
                });
            });
            $$(".cart-drawer .item .cart-qty--remove").forEach(btn => {
                btn.addEventListener("click", (e) => {
                    e.preventDefault();
                    if (btn.classList.contains("disabled")) return;
                    const input = btn.closest(".cluster.quantity").querySelector("input");
                    this.setRowQuantity(btn.closest(".item"), Math.max(1, parseInt(input.value) - 1));
                });
            });
            
            $$(".cart-drawer .remove").forEach(btn => {
                btn.addEventListener("click", (e) => {
                    e.preventDefault();
                    this.setRowQuantity(e.target.closest(".item"), 0);
                });
            });
            $$(".cart-drawer .item a:not(.remove)").forEach(link => {
                const linkPath = new URL(link.href, window.location.origin).pathname;
                const alreadyOnThisProductPage = window.location.pathname === linkPath;
                if (alreadyOnThisProductPage) link.classList.add("disabled");

                link.addEventListener("click", (e) => {
                    // 1. If any input is focused, blur it and bail out.
                    const focusedInput = document.activeElement;
                    if (focusedInput && focusedInput.tagName === 'INPUT') {
                        e.preventDefault();
                        focusedInput.blur();
                        return;
                    }
                    
                    // 2. Also don't navigate if already on this product page.
                    if (alreadyOnThisProductPage) {
                        e.preventDefault();
                        return;
                    }
                    
                    // Otherwise, fire away!
                });
            });
            
            $$(".cart-drawer .current-selection").forEach(btn => {
                btn.addEventListener("click", (e) => {
                    e.preventDefault();
                    const selection = btn.closest(".selection");
                    selection.classList.toggle("selected");
                    setTimeout(() => {
                        const selector = btn.closest(".payment-type");
                        if (!selector) { return; }
                        selector.scrollIntoView({ behavior: "smooth" });
                    }, 210);
                });
            });
            $$(".cart-drawer .options .option").forEach(option => {
                option.addEventListener("click", (e) => {
                    e.preventDefault();
                    
                    if (option.classList.contains('selected')) {
                        const selection = option.closest('.selection');
                        selection.classList.remove('selected');
                        return;
                    }

                    const line = option.dataset.line;
                    const planId = option.dataset.planId ? parseInt(option.dataset.planId) : null;

                    // Multi-line row (Buy X Get Y split): a per-line plan change would leave
                    // the other lines behind, so re-add the whole group under the new plan.
                    const row = option.closest(".item");
                    const keys = row ? JSON.parse(row.dataset.keys || "[]") : [];
                    if (keys.length > 1) {
                        this.changeGroupPlan(keys, parseInt(row.dataset.variantId), parseInt(row.dataset.total) || 1, planId);
                        return;
                    }
                    this.updateCartItemPlan(line, planId);
                });
            });
        },

        // -- Grouped Rows (Buy X Get Y) --

        // Collapses the presented cart lines into one row per variant + plan + properties.
        // Shopify splits a Buy X Get Y variant into separate lines (paid / free / untagged)
        // with their own keys, and derives the free count from the variant's TOTAL. Each
        // group carries: item (a paid line to display), lines, keys, total, free, paid,
        // merged allocations by title, and bxgy ({buy, get}) when the sale covers it.
        groupCartItems(cart) {
            const groups = new Map();
            cart.items.forEach((item, index) => {
                const planId = item.selling_plan_allocation?.selling_plan?.id ?? '';
                const id = `${item.variant_id}|${planId}|${JSON.stringify(item.properties || {})}`;
                if (!groups.has(id)) {
                    groups.set(id, { id, item, lines: [], keys: [], total: 0, free: 0, paid: 0, allocations: [], bxgy: null });
                }
                const g = groups.get(id);
                const { finalUnit } = this.linePrices(item);
                g.lines.push(index + 1);
                g.keys.push(item.key);
                g.total += item.quantity;
                if (finalUnit <= 0) g.free += item.quantity;
                else if (this.linePrices(g.item).finalUnit <= 0) g.item = item;   // prefer a paid line for display
                (item.line_level_discount_allocations || []).forEach(a => {
                    if (!(a.amount > 0)) return;
                    const title = a.discount_application?.title || 'Discount';
                    const existing = g.allocations.find(x => x.title === title);
                    if (existing) existing.amount += a.amount; else g.allocations.push({ title, amount: a.amount });
                });
            });
            groups.forEach(g => {
                g.paid = g.total - g.free;
                g.bxgy = this.bxgyFor(g.item);
            });
            return [...groups.values()];
        },

        // {buy, get} when this cart item is in a Buy X Get Y offer per the theme config
        // (sale active, size listed, one-time unless the sale covers subscriptions).
        bxgyFor(item) {
            const promo = HiSnaps.config?.promo;
            if (!promo?.active) return null;
            const isSubscription = item.selling_plan_allocation != null;
            if (isSubscription && !promo.subscriptions) return null;
            const o = promo.offers || {};
            const size = (item.options_with_values || []).find(x => x.name.toLowerCase() === 'size')?.value || '';
            const listed = (o.bxgySizes || []).some(s => String(s).trim().toLowerCase() === size.trim().toLowerCase());
            const buy = parseInt(o.bxgyBuy) || 0;
            const get = parseInt(o.bxgyGet) || 0;
            return (listed && buy > 0 && get > 0) ? { buy, get } : null;
        },

        // Total units a Buy X Get Y row should hold for `paid` paid units.
        bxgyTotal(paid, bxgy) {
            return paid + Math.floor(paid / bxgy.buy) * bxgy.get;
        },

        // Quantity change from a row's stepper / input / Remove. `shown` is what the
        // customer typed: paid units for a Buy X Get Y row, total otherwise. 0 removes.
        async setRowQuantity(row, shown) {
            if (!row) return;
            const keys = JSON.parse(row.dataset.keys || "[]");
            const bxgy = row.dataset.bxgy ? JSON.parse(row.dataset.bxgy) : null;
            if (isNaN(shown)) return;
            if (shown <= 0) return this.setGroupQuantity(keys, 0);
            const total = bxgy ? this.bxgyTotal(shown, bxgy) : shown;
            if (keys.length === 1 && !bxgy) return this.updateCartItemQuantity(row.dataset.line, total);
            await this.setGroupQuantity(keys, total);
        },

        // Sets a group's total in one request: first key takes the total, the rest go
        // to 0. Shopify then re-splits the free units itself.
        async setGroupQuantity(keys, total) {
            if (!keys.length) return;
            this.showLoading(true);
            try {
                const cart = await this.postCartUpdates(this.groupUpdates(keys, total));
                if (!cart) throw new Error("Failed to update cart");
                await this.updateCartDrawer();
            } catch (error) {
                console.error('Error updating cart:', error);
                this.showCartError();
            } finally {
                this.showLoading(false);
            }
        },

        // Plan change on a multi-line group: drop all its lines, re-add under the plan.
        async changeGroupPlan(keys, variantId, total, planId) {
            this.showLoading(true);
            try {
                const updates = {};
                keys.forEach(k => { updates[k] = 0; });
                await fetch("/cart/update.js", {
                    method: 'POST', headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({ updates })
                });
                const line = { id: variantId, quantity: total };
                if (planId) line.selling_plan = planId;
                await fetch("/cart/add.js", {
                    method: 'POST', headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({ items: [line] })
                });
                await this.updateCartDrawer();
            } catch (error) {
                console.error('Error changing plan:', error);
                this.showCartError();
            } finally {
                this.showLoading(false);
            }
        },

        // Tops up Buy X Get Y rows so the earned free units are in the cart: 3 paid
        // pouches -> 4 in the cart. Never reduces. Returns true when it changed the
        // cart (caller refetches). Safety: after a top-up, the free count must have
        // gone up — if Shopify didn't make the extra unit free (discount missing or
        // not applicable), the top-up is reverted and no further attempts are made
        // this session, so a customer is never charged for a unit the theme added.
        async reconcileFreeUnits(cart) {
            if (HiSnaps.freeUnitsBroken) return false;
            const groups = this.groupCartItems(cart);
            for (const g of groups) {
                if (!g.bxgy) continue;
                const expected = this.bxgyTotal(g.paid, g.bxgy);
                if (expected <= g.total) continue;

                const before = g.total;
                const res = await this.postCartUpdates(this.groupUpdates(g.keys, expected));
                if (!res) return false;
                const after = this.groupCartItems(res).find(x => x.id === g.id);
                if (!after || after.free <= g.free) {
                    // Not free — put it back and stop trying.
                    HiSnaps.freeUnitsBroken = true;
                    console.warn("Free units not granted by Shopify; reverting top-up.", g.id);
                    if (after) await this.postCartUpdates(this.groupUpdates(after.keys, before));
                }
                return true;
            }
            return false;
        },
        groupUpdates(keys, total) {
            const updates = {};
            keys.slice(1).forEach(k => { updates[k] = 0; });
            updates[keys[0]] = Math.max(0, total);
            return updates;
        },
        async postCartUpdates(updates) {
            try {
                const response = await fetch("/cart/update.js", {
                    method: 'POST', headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({ updates })
                });
                return response.ok ? response.json() : null;
            } catch (e) { return null; }
        },
        updateUpsellBanner(cart, newTotal) {
            const product = HiSnaps.getProduct();
            if (!product) return;
            
            if (product.staticAddBanner) {
                const addBanner = $1(".cart-drawer .upsell-banner.add");
                if (addBanner) {
                    addBanner.classList.remove("hide");
                }
                return;
            }

            const upsellBanner = $1(".cart-drawer .upsell-banner.upgrade");
            if (!upsellBanner) return;

            const upsellVariantId = product.upsell;
            if (!upsellVariantId) {
                upsellBanner.classList.add("hide");
                return;
            }
            const upsellVariant = product.variants.find(v => v.id === upsellVariantId);
            if (!upsellVariant) {
                upsellBanner.classList.add("hide");
                return;
            }

            const hasUpsell = cart.items.some(item => item.variant_id === upsellVariantId);
            const hasNonUpsell = cart.items.some(item => item.variant_id !== upsellVariantId);
            
            if (hasNonUpsell && newTotal < upsellVariant.price && !hasUpsell) {
                upsellBanner.classList.remove("hide");
            } else {
                upsellBanner.classList.add("hide");
            }
        },

        // -- Cart Item Management --

        async updateCartItem(line, updates) {
            this.showLoading(true);
            
            try {
                const response = await fetch("/cart/change.js", {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({ 
                        line: parseInt(line), 
                        ...updates 
                    })
                });
                
                if (!response.ok) {
                    throw new Error(`Failed to update cart: ${response.status}`);
                }
                
                await this.updateCartDrawer();
                
            } catch (error) {
                console.error('Error updating cart:', error);
                this.showCartError();
            } finally {
                this.showLoading(false);
            }
        },
        async updateCartItemQuantity(line, quantity) {
            if (quantity < 1) return;
            await this.updateCartItem(line, { quantity });
        },
        async removeCartItem(line) {
            await this.updateCartItem(line, { quantity: 0 });
        },
        async updateCartItemPlan(line, planId) {
            // Get the current quantity from the input field.
            // Otherwise Shopify resets it to 1 on any plan change.
            const quantityInput = $1(`.cart-drawer .quantity input[data-line="${line}"]`);
            const currentQuantity = quantityInput ? parseInt(quantityInput.value) : 1;

            await this.updateCartItem(line, {
                quantity: currentQuantity,
                selling_plan: planId || null
            });
        },

        // -- Cart Line Prices --
        // One place that knows which Ajax cart fields mean what. All cents.
        //   originalUnit / originalLine: full one-time price (for subscriptions, the
        //     allocation's compare_at_price, i.e. the one-time price).
        //   finalUnit / finalLine: what the customer pays — Shopify's final_* fields,
        //     which include subscription pricing and automatic/code discounts.
        linePrices(item) {
            const qty = item.quantity || 1;
            const isSubscription = item.selling_plan_allocation != null;
            const originalUnit = isSubscription
                ? item.selling_plan_allocation.compare_at_price
                : item.original_price;
            const finalUnit = (item.final_price != null) ? item.final_price
                : (isSubscription ? item.selling_plan_allocation.per_delivery_price : item.price);
            const finalLine = (item.final_line_price != null) ? item.final_line_price : finalUnit * qty;
            return { originalUnit, finalUnit, originalLine: originalUnit * qty, finalLine };
        },

        // -- Price Formatting --
        priceString(valueInCents) {
            const dollars = Math.floor(valueInCents / 100);
            const cents = String(valueInCents % 100).padStart(2, "0");
            return `$${dollars}.${cents}`
        },

        // -- Loading + Errors --
        showLoading(isLoading) {
            if (isLoading) $1(".cart-drawer").classList.add("loading");
            else $1(".cart-drawer").classList.remove("loading");

            HiSnaps.allCartBtns.forEach(btn => {
                if (isLoading) {
                    btn.classList.add("loading");
                } else {
                    btn.classList.remove("loading");
                }
            });
        },
        showCartError() {
            // TODO: 
        },
        
        // -- Handle Defaults --
        initializeDefaults() {
            const product = HiSnaps.getProduct();
            if (!product) return;
        
            const urlParams = new URLSearchParams(window.location.search);
            
            // Variants
            if (product.variants.length > 0) {
                const defaultVariant = product.variants.find(v => 
                    v.id === product.defaultVariantId
                );
                const sizePos = product.optionPositions?.size || 'option1';
                const flavorPos = product.optionPositions?.flavor || 'option2';
                
                // First, check for Shopify standard variant param.
                const variantParam = urlParams.get("variant");
                if (variantParam) {
                    const variantId = parseInt(variantParam);
                    const variant = product.variants.find(v => v.id === variantId);
                    if (variant) {
                        // Select the size and flavor buttons for this variant
                        const sizeValue = variant[sizePos];
                        const flavorValue = variant[flavorPos];
                        
                        if (sizeValue) {
                            const sizeBtn = $1(`[data-option="size"][data-value="${sizeValue}"]`);
                            sizeBtn?.classList.add("selected");
                        }
                        if (flavorValue) {
                            const flavorBtn = $1(`[data-option="flavor"][data-value="${flavorValue}"]`);
                            flavorBtn?.classList.add("selected");
                        }
                    }
        
                // Then, check for our nice human readable ones.
                } else {
                    const sizeParam = urlParams.get("size");
                    const flavorParam = urlParams.get("flavor");
                    
                    const findBtn = (option, value) => {
                        if (!value) return null;
                        const lower = value.toLowerCase();
                        return Array.from($$(`[data-option="${option}"]`))
                            .find(el => el.dataset.value.toLowerCase() === lower) || null;
                    };

                    // Flavor
                    let flavorBtn;
                    if (flavorParam) {
                        flavorBtn = findBtn("flavor", flavorParam);
                    } else if (defaultVariant && defaultVariant[flavorPos]) {
                        flavorBtn = findBtn("flavor", defaultVariant[flavorPos]);
                    }
                    if (!flavorBtn) flavorBtn = $1('[data-option="flavor"]');
                    flavorBtn?.classList.add("selected");

                    // Size
                    let sizeBtn;
                    if (sizeParam) {
                        sizeBtn = findBtn("size", sizeParam);
                    } else if (defaultVariant && defaultVariant[sizePos]) {
                        sizeBtn = findBtn("size", defaultVariant[sizePos]);
                    }
                    if (!sizeBtn) sizeBtn = $1('[data-option="size"]');
                    sizeBtn?.classList.add("selected");
                }
        
            // No variants? Just use the default.
            } else {
                HiSnaps.selectedVariantId = product.defaultVariantId;
            }
            
            // Purchase Type
            let purchaseType;
            let selectedPlanId = null;
            
            // First, use the Shopify selling_plan param.
            const sellingPlanParam = urlParams.get("selling_plan");
            if (sellingPlanParam && product.sellingPlans.length > 0) {
                selectedPlanId = parseInt(sellingPlanParam);
                const plan = product.sellingPlans.find(p => p.id === selectedPlanId);
                if (plan) {
                    purchaseType = "subscription";
                } else {
                    // Invalid plan ID, fall back to default.
                    purchaseType = product.defaultIsSubscription ? "subscription" : "onetime";
                    selectedPlanId = product.defaultPlanId;
                }
        
            // If not, check for our nice human readable one.
            } else {
                const purchaseParam = urlParams.get("purchase");
                if (purchaseParam) {
                    purchaseType = purchaseParam;
                    if (purchaseType.trim().toLowerCase() === 'subscription' && product.sellingPlans.length > 0) {
                        selectedPlanId = product.defaultPlanId || product.sellingPlans[0].id;
                    }
                } else {
                    // Invalid or unknown plan, fall back to default.
                    purchaseType = product.defaultIsSubscription ? "subscription" : "onetime";
                    selectedPlanId = product.defaultPlanId;

                    // Holiday Stock-Up: the sale option is the default while it's on.
                    // (Falls back to One-Time if the starting size isn't in the sale —
                    // see updatePromoDisplay.)
                    if (HiSnaps.config?.promo?.active && $1('[data-purchase-type="stockup"]')) {
                        purchaseType = "stockup";
                        selectedPlanId = null;
                    }
                }
            }
            
            const purchaseBtn = $1(`[data-purchase-type="${purchaseType}"]`);
            purchaseBtn?.classList.add("selected");
        
            // Update page state.
            HiSnaps.isSubscription = (purchaseType === "subscription");
            if (HiSnaps.isSubscription && product.sellingPlans.length > 0) {
                HiSnaps.selectedPlanId = selectedPlanId;
            }
        }
    }

});